import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import CloudinaryUpload from "../components/CloudinaryUpload";

const AMENITY_OPTIONS = ["Wifi", "AC", "Mess", "Laundry", "Power Backup", "CCTV", "Gym", "Geyser", "RO Water", "Housekeeping", "Study Hall", "Coworking"];
const ROOM_OPTIONS = ["single", "double", "shared", "triple"];

export default function ListProperty() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", city: "", address: "",
    price_per_month: "", gender: "co-ed",
    amenities: [], room_types: ["single"], images: [],
    college_nearby: "", distance_km: "",
    // PMS onboarding extras (Hostro = students + working pros + families)
    property_type: "pg",          // pg | hostel | co_living | apartment | flat
    deposit: "",                  // security deposit ₹
    lock_in_months: "0",
    food_included: false,
    food_meals: "0",              // 0/1/2/3 per day
    total_rooms: "",
    current_occupancy: "0",
    contact_phone: "",
    contact_whatsapp: "",
    house_rules: "",              // free-text
    open_to: ["students"],        // students | working_pros | families
  });
  const [imgUrl, setImgUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setForm(s => ({...s, [k]:v}));

  const toggle = (key, val) => set(key, form[key].includes(val) ? form[key].filter(x=>x!==val) : [...form[key], val]);
  const addImg = () => { if (imgUrl) { set("images", [...form.images, imgUrl]); setImgUrl(""); } };

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20">Loading...</div>;

  if (!user || typeof user !== "object") {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display font-black text-4xl">Sign in as an owner to list a PG.</h1>
        <div className="flex gap-3 justify-center mt-6">
          <Link to="/login" className="brut-btn">Sign in</Link>
          <Link to="/register" className="brut-btn pink">Register</Link>
        </div>
      </div>
    );
  }

  if (user.role !== "owner" && user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display font-black text-3xl">Listings are for PG Owners only.</h1>
        <p className="text-[#4A4A4A] mt-2">Your account role is &quot;{user.role}&quot;. Switch to an owner account.</p>
        <Link to="/" className="brut-btn mt-5 inline-flex">Back home</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price_per_month: parseFloat(form.price_per_month),
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
      };
      const { data } = await api.post("/properties", payload);
      toast.success("Property listed!");
      nav(`/properties/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10" data-testid="list-property-page">
      <span className="brut-tag lime">For PG, hostel, co-living & apartment owners</span>
      <h1 className="font-display font-black text-4xl md:text-5xl mt-3">List your property</h1>
      <p className="text-[#4A4A4A] mt-2">Hostro hosts students, working professionals & families. Zero brokerage. Built-in PMS — rent collection, occupancy, complaints, visitor entry.</p>

      <form onSubmit={submit} className="brut-card p-6 md:p-8 mt-8 space-y-5 bg-[#FDFCF8]">
        <div>
          <label className="label-eyebrow">Property title</label>
          <input required className="brut-input mt-1" value={form.title} onChange={e=>set("title", e.target.value)} placeholder="Sunrise PG for Boys near IIT" data-testid="lp-title"/>
        </div>
        <div>
          <label className="label-eyebrow">Description</label>
          <textarea required className="brut-input mt-1 min-h-[120px]" value={form.description} onChange={e=>set("description", e.target.value)} data-testid="lp-desc"/>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow">City</label>
            <input required className="brut-input mt-1" value={form.city} onChange={e=>set("city", e.target.value)} data-testid="lp-city"/>
          </div>
          <div>
            <label className="label-eyebrow">Address</label>
            <input required className="brut-input mt-1" value={form.address} onChange={e=>set("address", e.target.value)} data-testid="lp-address"/>
          </div>
          <div>
            <label className="label-eyebrow">Property type</label>
            <select className="brut-input mt-1" value={form.property_type} onChange={e=>set("property_type", e.target.value)} data-testid="lp-property-type">
              <option value="pg">PG (Paying Guest)</option>
              <option value="hostel">Hostel</option>
              <option value="co_living">Co-living</option>
              <option value="apartment">Apartment / Flat</option>
              <option value="hostro_managed">Hostro-managed</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow">Price per month (₹)</label>
            <input required type="number" className="brut-input mt-1" value={form.price_per_month} onChange={e=>set("price_per_month", e.target.value)} data-testid="lp-price"/>
          </div>
          <div>
            <label className="label-eyebrow">Security deposit (₹)</label>
            <input type="number" className="brut-input mt-1" value={form.deposit} onChange={e=>set("deposit", e.target.value)} placeholder="e.g. 10000" data-testid="lp-deposit"/>
          </div>
          <div>
            <label className="label-eyebrow">Lock-in (months)</label>
            <input type="number" min="0" className="brut-input mt-1" value={form.lock_in_months} onChange={e=>set("lock_in_months", e.target.value)} data-testid="lp-lockin"/>
          </div>
          <div>
            <label className="label-eyebrow">Gender</label>
            <select className="brut-input mt-1" value={form.gender} onChange={e=>set("gender", e.target.value)} data-testid="lp-gender">
              <option value="co-ed">Co-ed</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow">Nearby college</label>
            <input className="brut-input mt-1" value={form.college_nearby} onChange={e=>set("college_nearby", e.target.value)} data-testid="lp-college"/>
          </div>
          <div>
            <label className="label-eyebrow">Distance (km)</label>
            <input type="number" step="0.1" className="brut-input mt-1" value={form.distance_km} onChange={e=>set("distance_km", e.target.value)} data-testid="lp-distance"/>
          </div>
          <div>
            <label className="label-eyebrow">Total rooms</label>
            <input type="number" className="brut-input mt-1" value={form.total_rooms} onChange={e=>set("total_rooms", e.target.value)} placeholder="e.g. 24" data-testid="lp-total-rooms"/>
          </div>
          <div>
            <label className="label-eyebrow">Current occupancy</label>
            <input type="number" className="brut-input mt-1" value={form.current_occupancy} onChange={e=>set("current_occupancy", e.target.value)} placeholder="how many beds filled" data-testid="lp-occupancy"/>
          </div>
          <div>
            <label className="label-eyebrow">Owner phone</label>
            <input type="tel" className="brut-input mt-1" value={form.contact_phone} onChange={e=>set("contact_phone", e.target.value)} placeholder="+91 9876543210" data-testid="lp-phone"/>
          </div>
          <div>
            <label className="label-eyebrow">WhatsApp number</label>
            <input type="tel" className="brut-input mt-1" value={form.contact_whatsapp} onChange={e=>set("contact_whatsapp", e.target.value)} placeholder="for enquiries" data-testid="lp-whatsapp"/>
          </div>
          <div>
            <label className="label-eyebrow">Food included?</label>
            <select className="brut-input mt-1" value={form.food_included ? "yes" : "no"} onChange={e=>set("food_included", e.target.value === "yes")} data-testid="lp-food-included">
              <option value="no">No</option>
              <option value="yes">Yes — included in rent</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow">Meals per day</label>
            <select className="brut-input mt-1" value={form.food_meals} onChange={e=>set("food_meals", e.target.value)} data-testid="lp-meals">
              <option value="0">None</option>
              <option value="1">1 (lunch OR dinner)</option>
              <option value="2">2 (breakfast + dinner)</option>
              <option value="3">3 meals</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label-eyebrow">Open to</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {[{v:"students",l:"Students"},{v:"working_pros",l:"Working professionals"},{v:"families",l:"Families"}].map(o => (
              <button type="button" key={o.v} onClick={()=>toggle("open_to", o.v)}
                className={`brut-tag ${form.open_to.includes(o.v) ? "lime" : "white"} cursor-pointer`} data-testid={`open-${o.v}`}>{o.l}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-eyebrow">House rules (optional)</label>
          <textarea className="brut-input mt-1 min-h-[80px]" value={form.house_rules} onChange={e=>set("house_rules", e.target.value)} placeholder="Curfew 11 PM. No outside guests after 9 PM. No smoking..." data-testid="lp-rules"/>
        </div>

        <div>
          <label className="label-eyebrow">Amenities</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {AMENITY_OPTIONS.map(a => (
              <button type="button" key={a} onClick={()=>toggle("amenities", a)}
                className={`brut-tag ${form.amenities.includes(a) ? "lime" : "white"} cursor-pointer`} data-testid={`amenity-${a}`}>{a}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-eyebrow">Room types</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ROOM_OPTIONS.map(r => (
              <button type="button" key={r} onClick={()=>toggle("room_types", r)}
                className={`brut-tag ${form.room_types.includes(r) ? "pink" : "white"} cursor-pointer`} data-testid={`room-${r}`}>{r}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-eyebrow">Images</label>
          <div className="mt-2">
            <CloudinaryUpload
              folder="hostro/properties"
              multiple
              value={form.images}
              onChange={(urls) => set("images", urls)}
              accept="image/*"
              testId="lp-images"
              label=""
            />
          </div>
          <details className="mt-3">
            <summary className="text-xs text-[#4A4A4A] cursor-pointer">Or paste URLs manually</summary>
            <div className="flex gap-2 mt-2">
              <input className="brut-input" value={imgUrl} onChange={e=>setImgUrl(e.target.value)} placeholder="https://..." data-testid="lp-img-input"/>
              <button type="button" className="brut-btn white" onClick={addImg} data-testid="lp-img-add"><Plus size={16}/></button>
            </div>
          </details>
        </div>

        <button disabled={busy} className="brut-btn w-full" data-testid="lp-submit">{busy ? "Publishing..." : "Publish listing"}</button>
      </form>
    </div>
  );
}
